import { useEffect, useState } from 'react';
import Head from 'next/head';
import { SnackbarProvider } from 'notistack';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { queryClient } from '~/lib/query-client';

import '~/public/sass/style.scss';
import '~/styles/tailwind.css';
import 'react-inner-image-zoom/lib/styles.min.css';
import '~/styles/zoom-overrides.css';
import '~/styles/theme-riode.css';
import LayoutDesktop from '../layouts/desktop';
import LayoutMobile from '../layouts/mobile';

import {
  ApiProvider,
  ProgramProvider,
  AuthProvider,
  ApiAuthProvider,
  GeneralProvider,
  CartProvider,
} from 'context';
import { StoreAnalyticsBridge } from '~/components/analytics/StoreAnalyticsBridge';
import AppErrorBoundary from '~/components/common/app-error-boundary';
import { useRouter } from 'next/router';

const App = ({ Component, pageProps }) => {
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary resetKey={router.asPath}>
        <SnackbarProvider
          maxSnack={50}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          hideIconVariant
          autoHideDuration={3000}
        >
          <ApiProvider>
            <ProgramProvider>
              <AuthProvider>
                <GeneralProvider>
                  <ApiAuthProvider>
                    <CartProvider>
                      <StoreAnalyticsBridge>
                        <Head>
                          <meta
                            name="viewport"
                            content="width=device-width, initial-scale=1, shrink-to-fit=no"
                          />
                          <title>Tienda-Premia Tu Esfuerzo</title>
                          <meta name="description" content="Tienda - Premia Tu Esfuerzo" />
                        </Head>
                        {isMobile ? (
                          <LayoutMobile>
                            <Component {...pageProps} />
                          </LayoutMobile>
                        ) : (
                          <LayoutDesktop>
                            <Component {...pageProps} />
                          </LayoutDesktop>
                        )}
                      </StoreAnalyticsBridge>
                    </CartProvider>
                  </ApiAuthProvider>
                </GeneralProvider>
              </AuthProvider>
            </ProgramProvider>
          </ApiProvider>
        </SnackbarProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </AppErrorBoundary>
    </QueryClientProvider>
  );
};

export default App;
